import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              Welcome to 99prop.com ("Company", "We", "Our", "Us", "Platform").
              Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit and use our website and services.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              By accessing or using 99prop.com, you agree to the terms of this Privacy Policy.
            </p>
          </div>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">1. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed">
              We may collect the following categories of information:
            </p>
            
            <div className="ml-4 space-y-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">A. Personal Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Full Name</li>
                  <li>Phone Number</li>
                  <li>Email Address</li>
                  <li>City / Location</li>
                  <li>Property details submitted</li>
                  <li>Budget range</li>
                  <li>Buying or selling preferences</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">B. Financial & Preference Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Loan preference</li>
                  <li>Risk appetite</li>
                  <li>Investment goals</li>
                  <li>Holding period</li>
                  <li>ROI expectations</li>
                  <li>Purpose of purchase (self-use / investment / rental / flipping)</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-2 italic">
                  This information is collected strictly for analytical, advisory, and service-related purposes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">C. Technical & Usage Data</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>IP address</li>
                  <li>Browser type</li>
                  <li>Device type</li>
                  <li>Operating system</li>
                  <li>Pages visited</li>
                  <li>Time spent on website</li>
                  <li>Cookies and tracking data</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">2. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed">We use your information to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Generate property valuation estimates</li>
              <li>Provide buyer strategy recommendations</li>
              <li>Prepare ROI illustrations and analytical reports</li>
              <li>Improve our scoring models and analytical systems</li>
              <li>Contact you regarding inquiries or property opportunities</li>
              <li>Send relevant updates related to your submitted preferences</li>
              <li>Improve website performance and user experience</li>
              <li>Maintain internal records and prevent misuse</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              We do not sell your personal information.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">3. AI & Analytical Processing</h2>
            <p className="text-gray-700 leading-relaxed">
              Certain valuation outputs and buyer strategy reports are generated using:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>AI-powered analytical tools</li>
              <li>Algorithm-based scoring models</li>
              <li>Third-party aggregated data sources</li>
              <li>Market trend analytics</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Your inputs may be processed automatically to generate analytical insights.
            </p>
            <p className="text-gray-700 leading-relaxed italic">
              While we aim for accuracy, outputs are indicative in nature and should not be considered financial, legal, or investment advice.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">4. Data Sharing & Disclosure</h2>
            <p className="text-gray-700 leading-relaxed">
              We may share information in the following limited situations:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>With internal team members for service fulfillment</li>
              <li>With trusted service providers such as hosting providers, CRM systems, and analytics tools</li>
              <li>If required by applicable law, regulation, or legal process</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mt-4">
              We do not sell, rent, or trade your personal data to third parties for unrelated marketing purposes.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement reasonable administrative and technical safeguards to protect your data from:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Unauthorized access</li>
              <li>Misuse</li>
              <li>Alteration</li>
              <li>Disclosure</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              However, no online platform can guarantee absolute security.
              Users acknowledge that transmission of information over the internet carries inherent risks.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">6. Cookies & Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed">
              99prop.com may use cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Improve user experience</li>
              <li>Analyze website traffic</li>
              <li>Remember user preferences</li>
              <li>Enhance platform functionality</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You may disable cookies through your browser settings, although certain features of the website may not function properly.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">7. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We are not responsible for the privacy practices or content of such external websites.
              Users are encouraged to review the privacy policies of third-party platforms independently.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">8. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain personal information only for as long as necessary to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide our services</li>
              <li>Maintain internal records</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Users may request deletion of their data by contacting us.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">9. User Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              Subject to applicable laws, users may:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Request access to their personal data</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of personal data</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Requests can be sent to: <a href="mailto:info@99prop.com" className="text-brand-500 hover:underline font-semibold">info@99prop.com</a>
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              99prop.com is not intended for individuals under the age of 18.
              We do not knowingly collect personal information from minors.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">11. Policy Updates</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to update or modify this Privacy Policy at any time.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Any changes will be reflected on this page with an updated effective date.
              Continued use of the Platform after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">12. Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy shall be governed by and construed in accordance with the laws of India.
            </p>
            <p className="text-gray-700 leading-relaxed font-semibold">
              Subject to jurisdiction: Ghaziabad, Uttar Pradesh
            </p>
          </section>

          <section className="space-y-4 pt-6 border-t-2 border-gray-200">
            <p className="text-gray-700 leading-relaxed">
              If you have any questions regarding this Privacy Policy, please contact:
            </p>
            <p className="text-brand-500 font-semibold text-lg">
              <a href="mailto:info@99prop.com" className="hover:underline">info@99prop.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
